import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { ModernCaptcha } from "@/components/ModernCaptcha";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

import { 
  Send, 
  User, 
  Mail, 
  Building2, 
  Globe, 
  Target, 
  Wallet, 
  MessageSquare,
} from "lucide-react";

import { z } from "zod";

const diagnosisSchema = z.object({
  name: z.string().trim().min(1, { message: "Name cannot be empty" }).max(100, { message: "Name must be less than 100 characters" }),
  email: z.string().trim().email({ message: "Invalid email address" }).max(255, { message: "Email must be less than 255 characters" }),
  company: z.string().trim().max(100).optional().or(z.literal("")),
  website: z.string().trim().url({ message: "Website must be a valid URL" }).max(2048).optional().or(z.literal("")),
  objective: z.string().trim().max(200).optional().or(z.literal("")),
  budget_level: z.enum(["A","B","C","D"]).optional().or(z.literal("")),
  message: z.string().trim().max(1000, { message: "Message must be less than 1000 characters" }).optional().or(z.literal("")),
});

interface DiagnosisFormProps {
  language: 'es' | 'en';
  sourcePage?: string;
}

export const DiagnosisForm = ({ language, sourcePage }: DiagnosisFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLock = useRef(false);
  const [isVerified, setIsVerified] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [verificationToken, setVerificationToken] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    website: '',
    objective: '',
    budget_level: '',
    message: ''
  });

  const content = {
    es: {
      title: "Agenda tu reunión inicial gratuita de 30 minutos",
      subtitle: "Completa el formulario para darme contexto antes de nuestra reunión.",

      form: {
        name: "Nombre completo",
        namePlaceholder: "Tu nombre",
        email: "Email corporativo",
        emailPlaceholder: "tu@empresa.com",
        company: "Empresa / Industria",
        companyPlaceholder: "Ej. Retail, Legal, Salud",
        website: "URL del sitio web",
        websitePlaceholder: "https://tusitio.com",
        objective: "Objetivo principal",
        objectivePlaceholder: "Más leads, ventas, posicionamiento…",
        budget: "Presupuesto estimado",
        budgetOptions: [
          { value: "A", label: "$1k - $3k/mes" },
          { value: "B", label: "$3k - $5k/mes" },
          { value: "C", label: "$5k - $10k/mes" },
          { value: "D", label: "$10k+/mes" }
        ],
        message: "Contexto adicional",
        messagePlaceholder: "Cuéntame brevemente sobre tu proyecto y los desafíos actuales con tu sitio web...",
        submit: "Enviar solicitud"
      },
      benefits: [
        "Respuesta en horario laboral (lunes a viernes)",
        "Sin compromiso ni costos ocultos",
        "Conversación 1:1 con Luis ROC, en español o inglés"
      ],
      trust: "Tu información es confidencial y solo se usa para preparar la reunión"

    },
    en: {
      title: "Request your free 30-minute initial consultation",
      subtitle: "Complete the form so I have context before our meeting.",

      form: {
        name: "Full name",
        namePlaceholder: "Your name",
        email: "Corporate email",
        emailPlaceholder: "you@company.com",
        company: "Company / Industry",
        companyPlaceholder: "e.g., Retail, Legal, Healthcare",
        website: "Website URL",
        websitePlaceholder: "https://yoursite.com",
        objective: "Main objective",
        objectivePlaceholder: "More leads, sales, rankings…",
        budget: "Estimated budget",
        budgetOptions: [
          { value: "A", label: "$1k - $3k/month" },
          { value: "B", label: "$3k - $5k/month" },
          { value: "C", label: "$5k - $10k/month" },
          { value: "D", label: "$10k+/month" }
        ],
        message: "Additional context",
        messagePlaceholder: "Tell me briefly about your project and current challenges with your website...",
        submit: "Submit request"
      },
      benefits: [
        "Reply during business hours (Monday to Friday)",
        "No commitment or hidden costs",
        "1:1 conversation with Luis ROC, in English or Spanish"
      ],
      trust: "Your information is confidential and only used to prepare the meeting"
    }

  };

  const t = content[language];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Guarda síncrona: el estado de React no se aplica a tiempo ante un doble clic rápido.
    if (submitLock.current) return;

    const parsed = diagnosisSchema.safeParse(formData);
    if (!parsed.success) {
      const msg = parsed.error.issues[0]?.message || (language === 'es' ? 'Datos inválidos' : 'Invalid data');
      toast.error(msg);
      return;
    }

    if (!isVerified) {
      toast.error(language === 'es' ? 'Por favor completa la verificación de seguridad' : 'Please complete security verification');
      return;
    }

    submitLock.current = true;
    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('send-lead-email', {
        body: {
          ...parsed.data,
          source_page: sourcePage || window.location.pathname,
          form_type: 'diagnosis',
          verification_token: verificationToken
        }
      });

      if (error) throw error;

      trackEvent('form_submit', {
        form_type: 'diagnosis',
        source_page: sourcePage || window.location.pathname,
      });

      toast.success(language === 'es' ? 'Formulario enviado exitosamente. Te contactaremos pronto.' : 'Form submitted successfully. We\'ll contact you soon.');
      
      setFormData({
        name: '',
        email: '',
        company: '',
        website: '',
        objective: '',
        budget_level: '',
        message: ''
      });
      setIsVerified(false);
      setVerificationToken('');
      
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(language === 'es' ? 'Error al enviar el formulario. Inténtalo de nuevo.' : 'Error submitting form. Please try again.');
    } finally {
      submitLock.current = false;
      setIsSubmitting(false);
    }
  };

  const handleVerification = (token: string, honeypotData: any) => {
    setIsVerified(true);
    setVerificationToken(token);
  };

  const handleInputChange = (field: string, value: string) => {
    if (!hasStarted) {
      setHasStarted(true);
      trackEvent('form_start', { form_type: 'diagnosis', source_page: sourcePage || window.location.pathname });
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };


  const inputFields = [
    { id: 'name', type: 'text', icon: User, label: t.form.name, placeholder: t.form.namePlaceholder, required: true },
    { id: 'email', type: 'email', icon: Mail, label: t.form.email, placeholder: t.form.emailPlaceholder, required: true },
    { id: 'company', type: 'text', icon: Building2, label: t.form.company, placeholder: t.form.companyPlaceholder, required: false },
    { id: 'website', type: 'url', icon: Globe, label: t.form.website, placeholder: t.form.websitePlaceholder, required: false },
    { id: 'objective', type: 'text', icon: Target, label: t.form.objective, placeholder: t.form.objectivePlaceholder, required: false },
  ];

  return (
    <div className="max-w-5xl mx-auto">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
        {/* Left Column - Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="min-w-0 space-y-6 lg:col-span-2"
        >
          <div className="sticky top-24">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand">
              {language === 'es' ? '06 · Reserva' : '06 · Book'}
            </p>

            <h3 className="font-display mt-3 text-2xl font-bold text-white">
              {language === 'es' ? 'Antes de la reunión' : 'Before the meeting'}
            </h3>

            <ul className="mt-6 border-t border-slate-800">
              {t.benefits.map((benefit) => (
                <li key={benefit} className="flex gap-4 border-b border-slate-800 py-3 text-sm leading-relaxed text-slate-400">
                  <span className="mt-2 h-px w-4 flex-shrink-0 bg-brand" aria-hidden="true" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 border border-slate-800 p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                {language === 'es' ? 'Duración' : 'Duration'}
              </p>
              <p className="mt-2 font-display text-lg font-bold text-white">
                30 {language === 'es' ? 'minutos' : 'minutes'}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-slate-400">{t.trust}</p>
            </div>
          </div>

        </motion.div>

        {/* Right Column - Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="min-w-0 lg:col-span-3"
        >
          <Card className="border-slate-800 bg-slate-900/40">
            <CardContent className="p-6 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Input Fields Grid */}
                <div className="grid md:grid-cols-2 gap-6">
                  {inputFields.map((field, index) => (
                    <motion.div
                      key={field.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                      className="space-y-2"
                    >
                      <Label htmlFor={field.id} className="text-slate-300 flex items-center gap-2">
                        <field.icon className="w-4 h-4 text-slate-500" />
                        {field.label}
                        {field.required && <span className="text-brand">*</span>}
                      </Label>
                      <Input 
                        id={field.id}
                        type={field.type}
                        placeholder={field.placeholder}
                        value={formData[field.id as keyof typeof formData]}
                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                        required={field.required}
                        className="rounded-none border-slate-700 bg-slate-950/60 text-white placeholder:text-slate-500 focus:border-brand focus:ring-brand/20"
                      />
                    </motion.div>
                  ))}

                  {/* Budget Select */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.25 }}
                    className="space-y-2"
                  >
                    <Label htmlFor="budget" className="text-slate-300 flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-slate-500" />
                      {t.form.budget}
                    </Label>
                    <Select value={formData.budget_level} onValueChange={(value) => handleInputChange('budget_level', value)}>
                      <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white focus:border-brand focus:ring-brand/20">
                        <SelectValue placeholder={language === 'es' ? 'Seleccionar...' : 'Select...'} />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {t.form.budgetOptions.map((option) => (
                          <SelectItem 
                            key={option.value} 
                            value={option.value}
                            className="text-slate-300 focus:bg-slate-700 focus:text-white"
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </motion.div>
                </div>
                
                {/* Message Textarea */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="space-y-2"
                >
                  <Label htmlFor="message" className="text-slate-300 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-slate-500" />
                    {t.form.message}
                  </Label>
                  <Textarea 
                    id="message"
                    placeholder={t.form.messagePlaceholder}
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    className="min-h-[120px] bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-brand focus:ring-brand/20 resize-none"
                  />
                </motion.div>
                
                {/* Captcha */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.35 }}
                >
                  <ModernCaptcha 
                    onVerify={handleVerification}
                    language={language}
                    disabled={isSubmitting}
                  />
                </motion.div>

                {/* Submit Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                >
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="group w-full rounded-none bg-brand py-6 text-base text-white hover:bg-brand/90"
                    disabled={isSubmitting || !isVerified}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                        {language === 'es' ? 'Enviando...' : 'Sending...'}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2 group-hover:translate-x-1 transition-transform" />
                        {t.form.submit}
                      </>
                    )}
                  </Button>
                </motion.div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};
